import {
  BbContentPresenterContext,
  useBbContentPresenter,
} from '@Components/BigBrowse/bb2/BbContentPresenter';
import { MiniMap2 } from '@Map/MiniMap2/MiniMap2';
import { useMiniMap2Presenter } from '@Map/MiniMap2/MiniMap2Presenter';
import OwnableTheme from '@Theme';
import { observer } from 'mobx-react-lite';
import type { FC } from 'react';
import React from 'react';
import type { TextStyle, ViewStyle } from 'react-native';
import { Platform, StyleSheet, View, useWindowDimensions } from 'react-native';
import { Portal } from 'react-native-paper';
import styled from 'styled-components/native';

import { StyledMiniMapWrapper } from '../bb2/BbScreenContent';
import { MiniMapPointerArrow } from './MiniMapPointerArrow';
import { useShopFloorWelcomeBlurPresenter } from './ShopFloorWelcomeBlurPresenter';

const StyledOverlay = styled.TouchableOpacity`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
`;

const StyledContainer = styled.View`
  position: absolute;
  background: ${OwnableTheme.colors.black};
  ${Platform.OS === 'web' && `box-shadow: 0px 0px 50px rgba(0, 0, 0, 0.25);`}
  border-radius: 15px;
  padding: 47px;
  z-index: 21;
`;

const StyledSmallText = styled.Text`
  font-family: ${OwnableTheme.fonts.gilroy.bold};
  font-style: normal;
  font-size: 24px;
  line-height: 30px;
  letter-spacing: 0.03em;

  color: ${OwnableTheme.colors.white};
`;

type IProps = {
  showWelcomeBlurb: boolean;
  isInitializing: boolean;
};

type LayoutMode = 'desktop' | 'small' | 'ultraSmall';

const ShopFloorWelcomeBlurb: FC<IProps> = ({
  showWelcomeBlurb,
  isInitializing,
}) => {
  const { onWelcomeOverlayClick, welcomeBlurbShown } =
    useShopFloorWelcomeBlurPresenter();

  const { onMapClick } = useMiniMap2Presenter();

  const contentPresenter = useBbContentPresenter();

  const onMiniMapClick = () => {
    onWelcomeOverlayClick();
    onMapClick();
  };

  const { width } = useWindowDimensions();

  if (welcomeBlurbShown || !showWelcomeBlurb || isInitializing) {
    return null;
  }

  const { layoutMode, wrapper, text, secondLine } = getStyles(width);
  const showArrow = layoutMode === 'desktop';

  // the Portal's contents are rendered in a separate DOM tree, and thus missing the BbContentPresenterContext,
  // so we need to re-provide it inside it

  return (
    <Portal>
      <BbContentPresenterContext.Provider value={contentPresenter}>
        <StyledOverlay onPress={onWelcomeOverlayClick}>
          <StyledMiniMapWrapper>
            <MiniMap2 onClickOverride={onMiniMapClick} />
          </StyledMiniMapWrapper>

          {showArrow && (
            <View style={styles.arrow}>
              <MiniMapPointerArrow />
            </View>
          )}

          <StyledContainer style={wrapper}>
            <StyledSmallText style={text}>Welcome!</StyledSmallText>
            <StyledSmallText style={[text, secondLine]}>
              {`Use the map to explore\nthe departments.`}
            </StyledSmallText>
          </StyledContainer>
        </StyledOverlay>
      </BbContentPresenterContext.Provider>
    </Portal>
  );
};

export default observer(ShopFloorWelcomeBlurb);

const getLayoutMode = (width: number): LayoutMode => {
  if (width > 600) {
    return 'desktop';
  }
  if (width > 426) {
    return 'small';
  }

  return 'ultraSmall';
};

const getStyles = (
  width: number
): {
  layoutMode: LayoutMode;
  wrapper: ViewStyle;
  text: TextStyle;
  secondLine: TextStyle;
} => {
  const layoutMode = getLayoutMode(width);

  return { layoutMode, ...styleRecords[layoutMode] };
};

const styles = StyleSheet.create({
  arrow: { position: 'absolute', left: 105, bottom: 150 },

  desktopWrapper: { left: 226, bottom: 121, borderRadius: 21, padding: 47 },
  desktopText: { fontSize: 24, lineHeight: 30 },
  desktop2nd: { marginTop: 15 },

  smallWrapper: { left: 20, bottom: 157, borderRadius: 15, padding: 31 },
  smallText: { fontSize: 16, lineHeight: 20 },
  small2nd: { marginTop: 10 },

  ultraSmallWrapper: { left: 20, bottom: 125, borderRadius: 15, padding: 31 },
  ultraSmallText: { fontSize: 16, lineHeight: 20 },
});

const styleRecords: Record<
  LayoutMode,
  { wrapper: ViewStyle; text: TextStyle; secondLine: TextStyle }
> = {
  desktop: {
    wrapper: styles.desktopWrapper,
    text: styles.desktopText,
    secondLine: styles.desktop2nd,
  },
  small: {
    wrapper: styles.smallWrapper,
    text: styles.smallText,
    secondLine: styles.small2nd,
  },
  ultraSmall: {
    wrapper: styles.ultraSmallWrapper,
    text: styles.ultraSmallText,
    secondLine: styles.small2nd,
  },
};
